import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  CustomizingBullet,
  CustomizingBulletCheck,
  CustomizingBulletDot,
  CustomizingBulletSpinner,
  CustomizingCard,
  CustomizingColumn,
  CustomizingInsight,
  CustomizingInsightArea,
  CustomizingItemLabel,
  CustomizingList,
  CustomizingListItem,
  CustomizingMain,
  CustomizingSubtitle,
  CustomizingTitle,
  CustomizingTopProgress,
  CustomizingTopProgressFill,
  Header,
  Logo,
  Page,
  type CustomizingItemState,
} from './Onboarding.styled';

interface CustomizingProps {
  /**
   * True while the backend save is still in flight. The screen will hold on
   * the last "active" step until this flips false; only then can it move
   * into the "Workspace ready" terminal state.
   */
  apiPending: boolean;
  /**
   * Called once the animation has finished *and* the API call has resolved
   * successfully. The parent then routes the user into the authenticated
   * app.
   */
  onComplete: () => void;
}

const STEP_KEYS = [
  'onboarding.customizing.step.analyzing',
  'onboarding.customizing.step.connecting',
  'onboarding.customizing.step.preparing',
] as const;

const INSIGHT_KEYS = [
  'onboarding.customizing.insight.workspace',
  'onboarding.customizing.insight.tools',
  'onboarding.customizing.insight.environment',
] as const;

const STEP_DURATION_MS = 1700;
const TICK_INTERVAL_MS = 50;
const TOTAL_DURATION_MS = STEP_DURATION_MS * STEP_KEYS.length;
const INSIGHT_INTERVAL_MS = 2300;
const READY_HOLD_MS = 1000;

type ItemState = CustomizingItemState;

/**
 * Post-finish loader screen. Shows a thin top progress bar, a fixed checklist
 * that animates from spinner → check, and a rotating "insight" line. Once
 * the backend save resolves (`apiPending=false`) *and* the animation
 * completes, the screen flips to "Workspace ready" for a brief beat before
 * handing off to the authenticated app via `onComplete`.
 *
 * The animation runs on a real-time timer (vs. tying frames to the API
 * latency) so the user always sees a smooth experience even if the backend
 * is fast — the API just needs to finish *before* the animation does for
 * the screen to never block.
 */
const Customizing: React.FC<CustomizingProps> = ({
  apiPending,
  onComplete,
}) => {
  const intl = useIntl();
  const [activeStep, setActiveStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [insightIndex, setInsightIndex] = useState(0);
  const [animationDone, setAnimationDone] = useState(false);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Step ticker: bump the active step every STEP_DURATION_MS until we hit
  // the last one (we leave the spinner on the last step so the user sees
  // "still working" if the API is slow).
  useEffect(() => {
    const interval = globalThis.setInterval(() => {
      setActiveStep((prev) =>
        prev >= STEP_KEYS.length - 1 ? prev : prev + 1,
      );
    }, STEP_DURATION_MS);
    return () => globalThis.clearInterval(interval);
  }, []);

  // Progress bar ticker: continuous fill over TOTAL_DURATION_MS.
  useEffect(() => {
    const start = Date.now();
    const tick = globalThis.setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, (elapsed / TOTAL_DURATION_MS) * 100);
      setProgress(pct);
      if (pct >= 100) {
        globalThis.clearInterval(tick);
        setAnimationDone(true);
      }
    }, TICK_INTERVAL_MS);
    return () => globalThis.clearInterval(tick);
  }, []);

  // Rotating insight underneath the divider.
  useEffect(() => {
    const interval = globalThis.setInterval(() => {
      setInsightIndex((i) => (i + 1) % INSIGHT_KEYS.length);
    }, INSIGHT_INTERVAL_MS);
    return () => globalThis.clearInterval(interval);
  }, []);

  // Both the local animation and the API call need to be done before we
  // declare victory. If the API is slow, we hold on the last spinner step
  // until it resolves.
  const completed = animationDone && !apiPending;

  // Brief hold on "Workspace ready" before handing off — gives the user
  // time to register the success state.
  useEffect(() => {
    if (!completed) return;
    const timer = globalThis.setTimeout(() => {
      onCompleteRef.current();
    }, READY_HOLD_MS);
    return () => globalThis.clearTimeout(timer);
  }, [completed]);

  const stateFor = (idx: number): ItemState => {
    if (completed) return 'done';
    if (idx < activeStep) return 'done';
    if (idx === activeStep) return 'active';
    return 'pending';
  };

  const titleId = completed
    ? 'onboarding.customizing.title.done'
    : 'onboarding.customizing.title';
  const subtitleId = completed
    ? 'onboarding.customizing.subtitle.done'
    : 'onboarding.customizing.subtitle';

  const renderedSteps = STEP_KEYS.map((key, idx) => {
    const state = stateFor(idx);
    return (
      <CustomizingListItem
        key={key}
        data-testid={`onboarding-customizing__step-${idx}`}
        data-state={state}
      >
        <CustomizingBullet $state={state} aria-hidden>
          {state === 'done' && (
            <CustomizingBulletCheck>
              <polyline points="2 6 5 9 10 3" />
            </CustomizingBulletCheck>
          )}
          {state === 'active' && <CustomizingBulletSpinner />}
          {state === 'pending' && <CustomizingBulletDot />}
        </CustomizingBullet>
        <CustomizingItemLabel $state={state}>
          {intl.formatMessage({ id: key })}
        </CustomizingItemLabel>
      </CustomizingListItem>
    );
  });

  return (
    <Page data-testid="onboarding-customizing">
      <CustomizingTopProgress
        role="progressbar"
        aria-label={intl.formatMessage({ id: 'onboarding.customizing.progress.label' })}
        aria-valuenow={Math.round(progress)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <CustomizingTopProgressFill $value={progress} />
      </CustomizingTopProgress>

      <Header>
        <Logo src="/img/icons/logo.svg" alt="Aibuild" />
      </Header>

      <CustomizingMain>
        <CustomizingColumn>
          <CustomizingTitle data-testid="onboarding-customizing__title">
            {intl.formatMessage({ id: titleId })}
          </CustomizingTitle>
          <CustomizingSubtitle>
            {intl.formatMessage({ id: subtitleId })}
          </CustomizingSubtitle>

          <CustomizingCard>
            <CustomizingList>{renderedSteps}</CustomizingList>

            <CustomizingInsightArea>
              <CustomizingInsight
                key={insightIndex}
                data-testid="onboarding-customizing__insight"
              >
                {`\u2192 ${intl.formatMessage({ id: INSIGHT_KEYS[insightIndex] })}`}
              </CustomizingInsight>
            </CustomizingInsightArea>
          </CustomizingCard>
        </CustomizingColumn>
      </CustomizingMain>
    </Page>
  );
};

export default Customizing;
